import {
  compact,
  flow,
  get,
  includes,
  isNaN,
  last,
  map,
  negate,
  pickBy,
} from 'lodash';
import * as R from 'remeda';
import { History, Location } from 'history';
import queryString, { ParseOptions } from 'query-string';
import { dataMatchingUrl, isPathBasedEmbeddedMode } from '../constants/config';
import { Url } from '../types/basic';
import {
  camelToSnake,
  convertKeys,
  convertStringValuesToSnake,
} from './keyConvertor';
import { getAuthToken } from './token';

export const asArray = <T>(value: T | T[] | null): T[] => {
  return value ? (R.isArray(value) ? value : [value]) : [];
};

export const asScalar = <T>(value: T | T[]): T | undefined => {
  return R.isArray(value) ? value[0] : value;
};

const withoutNullItems = (value: string | null | Array<string | null>) =>
  R.isArray(value) ? value.filter(R.isNonNull) : value;

export const parse = (string: string, parseOptions?: ParseOptions) =>
  R.mapValues(
    queryString.parse(string, { arrayFormat: 'comma', ...parseOptions }),
    withoutNullItems
  );
export const stringify = (query: Parameters<typeof queryString.stringify>[0]) =>
  queryString.stringify(query, { arrayFormat: 'comma' });

const isQuestionMark = (char: string): boolean => char !== '?';

const trimQuestionMark = (string: string): string =>
  string.split('').filter(isQuestionMark).join('');

export const getDatapointPathFromSearch = (search = ''): number[] =>
  flow(
    trimQuestionMark,
    parse,
    query => query['datapointPath'],
    path => (path ? ([] as string[]).concat(path) : []),
    (path: string[]) => map(path, Number)
  )(search);

// Both the legacy validation route (`/document/:id`) and the new split route
// (`/split/:id`) carry an annotation id in the very same position, and
// consumers of `getCurrentAnnotationId` treat them as "the same annotation" —
// navigating between them must not look like leaving the annotation (MAT-1958).
const ANNOTATION_ID_REGEX = /^\/(?:document|split)\/(\d+)/;
const ANNOTATION_ID_EMBEDDED_REGEX = /^\/embedded\/(?:document|split)\/(\d+)/;

// Deliberately NARROW: matches the legacy validation route only, so that logic
// which must not run on the new split screen can opt out of it.
const VALIDATION_PATH_REGEX = /^\/document\/\d+/;
const VALIDATION_PATH_EMBEDDED_REGEX = /^\/embedded\/document\/\d+/;

// Intentionally mode-agnostic: callers only ask "is this the split screen",
// and both the plain and the embedded shape answer yes.
const SPLIT_PATH_REGEX = /^\/(?:embedded\/)?split\/\d+/;

export const isSplitPathname = (path: string): boolean =>
  SPLIT_PATH_REGEX.test(path);

// The neighbors page (`/document/:id/neighbors`) is nested under the
// validation route but is not the validation screen: it must neither start
// the annotation nor have the selected datapoint synced into its URL.
const NEIGHBORS_PATH_REGEX = /^\/(?:embedded\/)?document\/\d+\/neighbors\/?$/;

export const isNeighborsPathname = (path: string): boolean =>
  NEIGHBORS_PATH_REGEX.test(path);

export const isValidationPathname = (path: string): boolean =>
  (isPathBasedEmbeddedMode()
    ? VALIDATION_PATH_EMBEDDED_REGEX
    : VALIDATION_PATH_REGEX
  ).test(path) && !isNeighborsPathname(path);

export const getCurrentAnnotationId = (path: string): number =>
  Number(
    (path.match(
      isPathBasedEmbeddedMode()
        ? ANNOTATION_ID_EMBEDDED_REGEX
        : ANNOTATION_ID_REGEX
    ) || [])[1]
  );
export const getAnnotationId = (path: string): number =>
  Number((path.match(/^.*\/annotations\/((\d+))/) || [])[1]);

const constructQuery = flow(
  convertKeys(camelToSnake),
  convertStringValuesToSnake([
    'search',
    'name',
    'phrase',
    'content.schema_id',
    'events',
  ]),
  stringify
);

export const constructUrl = (
  url: string,
  query: unknown,
  queryConstructor = constructQuery,
  hash?: string
) => {
  const strQuery = queryConstructor(query);
  const joint = strQuery ? (includes(url, '?') ? '&' : '?') : '';

  return `${url}${joint}${strQuery}${hash ? `#${hash}` : ''}`;
};

const supportedExtensionsUrls = [dataMatchingUrl];
const supportedExtensionsNames = ['data-matching', 'data-matching-legacy'];

const shouldAppendToken = (link: string) =>
  // URL check - useful also for local testing and static URLs
  supportedExtensionsUrls.includes(link) ||
  // more benevolent way for checking URL in hook templates
  (supportedExtensionsNames.some(extension => link.includes(extension)) &&
    (link.includes('.elis.') ||
      link.includes('.rossum.ai') ||
      link.includes('.rossum.app') ||
      link.includes('.r8.lol')));

export const buildAuthLink = (link: Url) =>
  shouldAppendToken(link) ? `${link}#token=${getAuthToken()}` : link;

export const getIDFromString = (URL: Url | undefined): number | undefined =>
  URL ? last(URL.split('/').map(Number).filter(negate(isNaN))) : undefined;

export const getIDsFromURLs = (urls: Url[]) =>
  compact(urls.map(getIDFromString));

// TODO: MEGAFUJ
export const setQueryCreator =
  (history: History) =>
  (queries: Record<string, unknown> & { pathname?: string }, hash?: string) => {
    const changingPage = !!queries['page'];
    const { pathname, ..._queries } = queries;
    const query = pickBy({
      ...parse(history.location.search),
      ...(changingPage ? _queries : { ..._queries, page: 1 }),
    });
    const search = stringify(query);

    history.push({
      pathname: pathname || history.location.pathname,
      search,
      hash,
      state: get(history, 'location.state'),
    });
  };

type ConstructDocumentUrlParams = {
  id?: number;
  pathname?: string;
  query?: object;
  hash?: string;
  nestedPath?: string;
};

export const constructDocumentUrl = ({
  id,
  pathname,
  query = {},
  hash = '',
  nestedPath = '',
}: ConstructDocumentUrlParams) =>
  constructUrl(
    pathname ||
      `${
        isPathBasedEmbeddedMode() ? '/embedded/document' : '/document'
      }/${id}${nestedPath}`,
    query,
    stringify,
    hash
  );

type EditModeUrlParams = {
  id: number;
  query?: object;
  hash?: string;
};

// Picks the split route when the flag is on, else falls back to the legacy
// `/edit` nested route. `isSplitEnabled` is passed in rather than read here
// so this stays a pure function usable from both hooks and epics.
export const getEditModeUrl = (
  isSplitEnabled: boolean,
  { id, query = {}, hash = '' }: EditModeUrlParams
): string =>
  isSplitEnabled
    ? constructUrl(
        `${isPathBasedEmbeddedMode() ? '/embedded/split' : '/split'}/${id}`,
        query,
        stringify,
        hash
      )
    : constructDocumentUrl({ id, nestedPath: '/edit', query, hash });

// Reverse mapping used by the flag-off safety-net redirect on the /split
// route itself.
export const getLegacyEditModeUrl = (params: EditModeUrlParams): string =>
  constructDocumentUrl({ ...params, nestedPath: '/edit' });

export const AUTH_CODE = 'authCode';

export const AUTO_LOGIN_SSO_PROVIDER_ID = 'auto_login_sso_provider_id';
export const SSO_FLOW = 'sso_flow';

// Single source of truth for the autologin params, shared by the strip side
// (removeAutoLoginParams) and the resolution side, so a future param can't be
// read in one place but left un-stripped in the other.
export const AUTO_LOGIN_PARAMS = [AUTO_LOGIN_SSO_PROVIDER_ID, SSO_FLOW];

// Strip the SSO autologin params from a query string. Filters raw key=value
// pairs rather than round-tripping through parse/stringify, which would both
// reorder the survivors (query-string sorts keys) and risk re-encoding values
// (e.g. comma-arrays). This preserves the original order and encoding.
export const removeAutoLoginParams = (search: string): string => {
  const query = search.startsWith('?') ? search.slice(1) : search;

  const kept = query
    .split('&')
    .filter(Boolean)
    .filter(pair => {
      const key = pair.split('=')[0] ?? '';
      return !AUTO_LOGIN_PARAMS.includes(key);
    });

  return kept.length > 0 ? `?${kept.join('&')}` : '';
};

export const removeTokenFromLocationHash = (location: Location) => {
  const { hash, ...originalLocation } = location;
  const {
    authToken,
    // use const with hardcoded string and rename extracted value
    [AUTH_CODE]: _authCode,
    ...restHash
  } = parse(hash);

  return {
    ...originalLocation,
    hash: stringify(restHash),
  };
};
